import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { CheckIcon } from "lucide-react";

type Module = components["schemas"]["ModuleResponse"];
type ClientModule = components["schemas"]["ClientModuleResponse"];

interface EditClientModulesDialogProps {
  clientId: string;
  heldModules: ClientModule[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (modules: ClientModule[]) => void;
}

export function EditClientModulesDialog({
  clientId,
  heldModules,
  open,
  onOpenChange,
  onUpdated,
}: EditClientModulesDialogProps) {
  const [catalogue, setCatalogue] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Keys the client held when the dialog opened. A non-grantable module (Preview
  // or Deprecated) can be kept if already held, but never newly granted.
  const heldKeys = useMemo(() => new Set(heldModules.map((m) => m.key)), [heldModules]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(heldModules.map((m) => m.key)));
    setReason("");

    let cancelled = false;
    async function fetchCatalogue() {
      setLoading(true);
      setLoadError(null);
      const { data, error } = await api.GET("/admin/modules");
      if (cancelled) return;
      if (error) {
        setLoadError("Failed to load the module catalogue.");
      } else {
        setCatalogue(data ?? []);
      }
      setLoading(false);
    }
    fetchCatalogue();
    return () => {
      cancelled = true;
    };
  }, [open, heldModules]);

  const areas = useMemo(() => {
    const tops = catalogue.filter((m) => !m.parentKey);
    tops.sort((a, b) => a.key.localeCompare(b.key));
    return tops.map((area) => ({
      area,
      features: catalogue
        .filter((m) => m.parentKey === area.key)
        .sort((a, b) => a.key.localeCompare(b.key)),
    }));
  }, [catalogue]);

  function canToggle(mod: Module): boolean {
    // Held modules can always be toggled (kept or revoked). Not-held modules can
    // only be granted when the catalogue marks them grantable.
    return heldKeys.has(mod.key) || mod.grantable === true;
  }

  function toggle(mod: Module) {
    setSelected((prev) => {
      const next = new Set(prev);
      const turningOn = !next.has(mod.key);
      if (turningOn) {
        next.add(mod.key);
        // A feature requires its area — pull the parent in automatically.
        if (mod.parentKey) next.add(mod.parentKey);
      } else {
        next.delete(mod.key);
        // Revoking an area revokes its features too.
        if (!mod.parentKey) {
          for (const m of catalogue) {
            if (m.parentKey === mod.key) next.delete(m.key);
          }
        }
      }
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    const { data, error } = await api.PUT("/admin/clients/{clientId}/modules", {
      params: { path: { clientId } },
      body: {
        modules: Array.from(selected),
        reason: reason.trim() || null,
      },
    });
    setSubmitting(false);

    if (error || !data) {
      const detail =
        (error as { detail?: string } | undefined)?.detail ?? "Failed to update modules.";
      toast.error(detail);
      return;
    }

    onUpdated(data);
    onOpenChange(false);
    toast.success("Modules updated.");
  }

  const held = heldModules.length;
  const chosen = selected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit modules</DialogTitle>
            <DialogDescription>
              Choose the complete set of modules this client should hold. Anything unchecked is
              revoked. Takes effect on the client's next request.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-5" />
              </div>
            ) : loadError ? (
              <p className="text-destructive text-sm">{loadError}</p>
            ) : (
              <div className="space-y-4">
                {areas.map(({ area, features }) => (
                  <div key={area.key} className="rounded-md border">
                    <ModuleRow
                      mod={area}
                      checked={selected.has(area.key)}
                      disabled={!canToggle(area)}
                      held={heldKeys.has(area.key)}
                      onToggle={() => toggle(area)}
                    />
                    {features.length > 0 && (
                      <div className="divide-y border-t">
                        {features.map((feat) => (
                          <ModuleRow
                            key={feat.key}
                            mod={feat}
                            checked={selected.has(feat.key)}
                            disabled={!canToggle(feat)}
                            held={heldKeys.has(feat.key)}
                            onToggle={() => toggle(feat)}
                            nested
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="space-y-2">
                  <Label htmlFor="modules-reason">Reason (optional)</Label>
                  <Textarea
                    id="modules-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Downgraded per contract change 2026-08"
                    rows={2}
                  />
                  <p className="text-muted-foreground text-xs">
                    Recorded in the audit trail against every module this change moves.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-6 flex-row items-center justify-between sm:justify-between">
            <span className="text-muted-foreground text-xs">
              {chosen} selected · {held} held
            </span>
            <Button type="submit" disabled={submitting || loading || !!loadError}>
              {submitting && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ModuleRow({
  mod,
  checked,
  disabled,
  held,
  onToggle,
  nested,
}: {
  mod: Module;
  checked: boolean;
  disabled: boolean;
  held: boolean;
  onToggle: () => void;
  nested?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        "flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors",
        nested && "pl-9",
        disabled ? "cursor-not-allowed opacity-60" : "hover:bg-accent/50",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border",
          checked ? "bg-primary border-primary text-primary-foreground" : "border-input",
        )}
      >
        {checked && <CheckIcon className="size-3" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{mod.displayName}</span>
          <code className="text-muted-foreground text-xs">{mod.key}</code>
          {mod.status && mod.status !== "Active" && (
            <Badge variant="outline" className="text-xs">
              {mod.status}
            </Badge>
          )}
          {mod.status && mod.status !== "Active" && !mod.grantable && !held && (
            <span className="text-muted-foreground text-xs">not grantable</span>
          )}
        </span>
        {mod.description && (
          <span className="text-muted-foreground mt-0.5 block text-xs">{mod.description}</span>
        )}
      </span>
    </button>
  );
}
